import { PresenterMeta } from '../shared/presenter';
import { IsPhaseAtLeast, OEP_OFFICE_HOURS_KIND, type IOEPResource, type IOEPSeasonTimeline, type OEPPhase } from 'agu_oep_core';

/** One OEP presentation in a session I nominate for (RLS, T5: Sessions/Presentations via Session Nominators). */
export interface OEPNomPresenterInput {
    PresentationID: string;
    Title: string;
    PresenterName: string;
    /** The presenter's Participant row (readable by the session's nominators, T5). */
    Institution: string | null;
    CareerStage: string | null;
    Format: string | null;
    Code: string | null;
    /** The newest Uploaded OEP Final file (nominators see finals only, never drafts — T5). */
    FinalFileID: string | null;
    /** I have a Submitted nomination for it this season. */
    Submitted: boolean;
}

export interface OEPNomSessionInput { SessionID: string; Code: string; Title: string; Presenters: OEPNomPresenterInput[] }

export interface NominateInputs {
    Phase: OEPPhase;
    Timeline: IOEPSeasonTimeline;
    SeasonCode: string;
    Sessions: OEPNomSessionInput[];
    /** Season.Resources.nominator. */
    Resources: IOEPResource[];
}

export interface OEPNomCta { Label: string; Enabled: boolean; Nominated: boolean }

export interface OEPNomPresenterRowVM {
    PresentationID: string;
    Title: string;
    Meta: string;
    FinalNote: string;
    /** The final file to open with "View final presentation", or null (no link). */
    FinalFileID: string | null;
    Cta: string;
    CtaEnabled: boolean;
    Nominated: boolean;
}

export interface OEPNomSessionVM { SessionID: string; Code: string; Title: string; Meta: string; Presenters: OEPNomPresenterRowVM[] }

export interface NominateVM {
    Intro: string;
    DeadlineLine: string;
    Tiles: IOEPResource[];
    Sessions: OEPNomSessionVM[];
}

type Stage = 'pre' | 'open' | 'closed';

/** The nomination window mapped onto phases (task 27 brief): before Nominating, Nominating, after. */
export function NominationStage(phase: OEPPhase): Stage {
    if (phase === 'Nominating') return 'open';
    return IsPhaseAtLeast(phase, 'Nominating') ? 'closed' : 'pre';
}

/** logic.js:346 cta, mapped to phases (task 27 brief). */
export function NominationCta(phase: OEPPhase, submitted: boolean, seasonCode: string): OEPNomCta {
    const stage = NominationStage(phase);
    if (stage === 'pre') return { Label: `Opens at ${seasonCode}`, Enabled: false, Nominated: false };
    if (submitted) return { Label: 'Nominated ✓', Enabled: true, Nominated: true };
    return stage === 'open' ? { Label: 'Nominate', Enabled: true, Nominated: false } : { Label: 'Not nominated', Enabled: false, Nominated: false };
}

/** logic.js:685-686 nomDeadlineLine; "in December" → the meeting's own label (task 27 brief). */
export function NominationDeadlineLine(phase: OEPPhase, seasonCode: string, t: IOEPSeasonTimeline): string {
    const d = t.Dates;
    const stage = NominationStage(phase);
    if (stage === 'pre') return `Nominations open at ${seasonCode}, ${d.Meeting.Label}.`;
    return stage === 'open' ? `Nomination deadline: submit by ${d.NominationsClose.Label}.` : `Nominations closed ${d.NominationsClose.Label}.`;
}

/** logic.js:345 finalNote; before feedback is released no final can exist yet (FinalUpload window). */
export function NominationFinalNote(phase: OEPPhase, finalFileID: string | null): { Text: string; FileID: string | null } {
    if (!IsPhaseAtLeast(phase, 'FeedbackReleased')) return { Text: 'Final presentations are uploaded before the meeting.', FileID: null };
    return finalFileID !== null ? { Text: 'Final presentation uploaded', FileID: finalFileID } : { Text: 'No final presentation uploaded yet', FileID: null };
}

/**
 * markup.html:671-682: the first two nominator resources as tiles. An office-hours tile
 * (Kind OEP_OFFICE_HOURS_KIND) keeps its authored Note; only when that is empty does it get the
 * prototype's note with the timeline's date: "Drop in 4 December, 11:00 a.m. ET, or watch the
 * recording." (markup.html:681).
 */
function tiles(resources: readonly IOEPResource[], t: IOEPSeasonTimeline): IOEPResource[] {
    return resources.slice(0, 2).map((r) => (r.Kind === OEP_OFFICE_HOURS_KIND && r.Note.trim().length === 0
        ? { ...r, Note: `Drop in ${t.Dates.OfficeHours.Label}, or watch the recording.` }
        : { ...r }));
}

/** markup.html:661-709 / logic.js:327-350, 641, 685-686 as one pure function. Every string cites its prototype line. */
export function BuildNominateVM(i: NominateInputs): NominateVM {
    return {
        // logic.js:641 nomIntro ("AGU Annual Meeting" kept; the deadline is the timeline's)
        Intro: `Watch the OEP presentations at the AGU Annual Meeting. Then, submit a nomination if you feel one of the presentations you saw should win an OEP award. Your Section committee will review nominations and pick the winners. The deadline for nominations is: ${i.Timeline.Dates.NominationsClose.Label}.`,
        DeadlineLine: NominationDeadlineLine(i.Phase, i.SeasonCode, i.Timeline),
        Tiles: tiles(i.Resources, i.Timeline),
        Sessions: i.Sessions.map((s) => ({
            SessionID: s.SessionID,
            Code: s.Code,
            Title: s.Title,
            // logic.js:328 meta '3 OEP presenters'
            Meta: `${s.Presenters.length} OEP ${s.Presenters.length === 1 ? 'presenter' : 'presenters'}`,
            Presenters: s.Presenters.map((p) => {
                const cta = NominationCta(i.Phase, p.Submitted, i.SeasonCode);
                const note = NominationFinalNote(i.Phase, p.FinalFileID);
                return {
                    PresentationID: p.PresentationID,
                    Title: p.Title,
                    Meta: PresenterMeta(p),
                    FinalNote: note.Text,
                    FinalFileID: note.FileID,
                    Cta: cta.Label,
                    CtaEnabled: cta.Enabled,
                    Nominated: cta.Nominated,
                };
            }),
        })),
    };
}
